import { useEffect, useState } from "react";
import { useGetReviews } from "@workspace/api-client-react";
import { useSeo } from "@/hooks/useSeo";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { openBookingModal } from "@/components/booking-modal";
import { Star } from "lucide-react";
import { REVIEW_STATS, formatReviewCount } from "@/data/review-stats";

const AGGREGATE = {
  ratingValue: 5.0,
  reviewCount: REVIEW_STATS.yandex.reviewCount,
  bestRating: 5,
  worstRating: 1,
};

const PLATFORMS: Record<string, { label: string; href: string }> = {
  yandex: { label: "Яндекс", href: REVIEW_STATS.yandex.url },
  "2gis": { label: "2ГИС", href: REVIEW_STATS.twoGis.url },
  google: { label: "Google", href: "https://maps.google.com/?cid=parusanakryshe" },
};

function formatDate(dateStr: string): string {
  try {
    return new Intl.DateTimeFormat("ru-RU", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(dateStr + (dateStr.length === 10 ? "T00:00:00" : "")));
  } catch {
    return dateStr;
  }
}

function StarRow({ rating, size = 14 }: { rating: number; size?: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          style={{ width: size, height: size }}
          className={n <= rating ? "fill-primary text-primary" : "text-primary/20"}
        />
      ))}
    </div>
  );
}

export default function Otzyvy() {
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState<"all" | "yandex" | "2gis">("all");

  useSeo({
    title: `Отзывы — Паруса на крыше | Рейтинг 5.0 · ${formatReviewCount(REVIEW_STATS.yandex.reviewCount)} отзывов`,
    description: "Читайте отзывы гостей ресторана «Паруса на крыше» в Санкт-Петербурге. Рейтинг 5.0 на Яндексе и 4.9 на 2ГИС. Оставьте свой отзыв.",
    keywords: "отзывы Паруса на крыше, ресторан отзывы СПб, Паруса на крыше рейтинг, ресторан на крыше Петербург отзывы",
    canonical: "/otzyvy",
  });
  useBreadcrumbs([{ name: "Отзывы гостей", path: "/otzyvy" }]);

  const { data: allReviews = [] } = useGetReviews();

  // JSON-LD schema
  useEffect(() => {
    if (!allReviews.length) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "Restaurant",
      "name": "Паруса на крыше",
      "url": "https://parusanakryshe.ru",
      "image": "https://parusanakryshe.ru/photos/view_sunset.jpg",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "ул. Марата, 5, 6 этаж",
        "addressLocality": "Санкт-Петербург",
        "postalCode": "191002",
        "addressCountry": "RU",
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": AGGREGATE.ratingValue,
        "reviewCount": AGGREGATE.reviewCount,
        "ratingCount": REVIEW_STATS.yandex.ratingCount,
        "bestRating": AGGREGATE.bestRating,
        "worstRating": AGGREGATE.worstRating,
      },
      "review": allReviews.slice(0, 6).map((r) => ({
        "@type": "Review",
        "author": { "@type": "Person", "name": r.author },
        "datePublished": r.date,
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": r.rating,
          "bestRating": 5,
          "worstRating": 1,
        },
        "reviewBody": r.text,
      })),
    };
    let el = document.getElementById("reviews-schema");
    if (!el) {
      el = document.createElement("script");
      el.id = "reviews-schema";
      el.setAttribute("type", "application/ld+json");
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(schema);
    return () => { el?.remove(); };
  }, [allReviews]);

  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  const visible = allReviews.filter((r) =>
    filter === "all" || r.platform === filter
  );

  return (
    <div className="w-full bg-background">

      {/* Hero */}
      <section className="pt-36 pb-24 border-b border-primary/10 relative overflow-hidden">
        <div className="absolute right-0 top-0 font-serif text-[340px] font-light text-primary/[0.03] leading-none select-none pointer-events-none translate-x-16 -translate-y-8">★</div>
        <div
          className="container mx-auto px-6 md:px-14 max-w-6xl relative z-10"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? "none" : "translateY(20px)", transition: "all 0.8s ease 0.2s" }}
        >
          <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-5">Гости о нас</div>
          <h1 className="text-5xl md:text-7xl font-serif font-light text-foreground mb-8 leading-tight">
            Что говорят<br /><span className="text-accent italic">гости</span>
          </h1>

          {/* Aggregate stats */}
          <div className="flex flex-wrap gap-10 items-center mt-10">
            <div className="flex flex-col gap-2">
              <StarRow rating={5} size={20} />
              <div className="flex items-baseline gap-3">
                <span className="font-serif text-5xl font-light text-foreground">5.0</span>
                <span className="text-muted-foreground text-sm font-light">{AGGREGATE.reviewCount.toLocaleString("ru-RU")} отзывов</span>
              </div>
              <span className="text-[9px] tracking-[0.3em] uppercase text-primary/40">Яндекс Карты</span>
            </div>
            <div className="w-[1px] h-16 bg-primary/15 hidden md:block" />
            <div className="flex flex-col gap-2">
              <StarRow rating={5} size={20} />
              <div className="flex items-baseline gap-3">
                <span className="font-serif text-5xl font-light text-foreground">4.9</span>
                <span className="text-muted-foreground text-sm font-light">{formatReviewCount(REVIEW_STATS.twoGis.reviewCount)} отзывов</span>
              </div>
              <span className="text-[9px] tracking-[0.3em] uppercase text-primary/40">2ГИС</span>
            </div>
            <div className="w-[1px] h-16 bg-primary/15 hidden md:block" />
            <div className="flex flex-col gap-3 max-w-xs">
              <p className="text-muted-foreground font-light text-sm leading-relaxed">Оставьте свой отзыв — это помогает другим гостям сделать выбор.</p>
              <div className="flex gap-3">
                <a href={PLATFORMS.yandex.href} target="_blank" rel="noreferrer"
                  className="border border-primary/30 text-accent text-[9px] tracking-[0.25em] uppercase px-5 py-2.5 hover:border-primary hover:bg-primary/10 transition-colors">
                  Яндекс
                </a>
                <a href={PLATFORMS["2gis"].href} target="_blank" rel="noreferrer"
                  className="border border-primary/30 text-accent text-[9px] tracking-[0.25em] uppercase px-5 py-2.5 hover:border-primary hover:bg-primary/10 transition-colors">
                  2ГИС
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter + Reviews */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">

          {/* Filter tabs */}
          <div className="flex gap-2 mb-12">
            {(["all", "yandex", "2gis"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-[9px] tracking-[0.3em] uppercase px-5 py-2.5 border transition-colors ${
                  filter === f
                    ? "border-primary bg-primary/10 text-accent"
                    : "border-primary/20 text-muted-foreground/60 hover:border-primary/40 hover:text-muted-foreground"
                }`}
              >
                {f === "all" ? "Все" : f === "yandex" ? "Яндекс" : "2ГИС"}
              </button>
            ))}
          </div>

          {/* Reviews grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {visible.map((review, i) => {
              const platform = PLATFORMS[review.platform] ?? PLATFORMS.yandex;
              return (
                <div
                  key={review.id}
                  className="border border-primary/15 p-8 flex flex-col gap-5 hover:border-primary/35 transition-colors"
                  style={{ opacity: loaded ? 1 : 0, transform: loaded ? "none" : "translateY(16px)", transition: `all 0.6s ease ${0.1 + i * 0.05}s` }}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-serif text-base text-foreground mb-1">{review.author}</div>
                      <div className="text-[9px] tracking-[0.2em] uppercase text-primary/40">{formatDate(review.date)}</div>
                    </div>
                    <div className="text-[8px] tracking-[0.2em] uppercase text-primary/30 mt-0.5">{platform.label}</div>
                  </div>

                  <StarRow rating={review.rating} />

                  {review.occasion && (
                    <div className="inline-flex self-start">
                      <span className="bg-primary/10 border border-primary/25 text-accent text-[8px] tracking-[0.2em] uppercase px-3 py-1.5">{review.occasion}</span>
                    </div>
                  )}

                  <p className="text-muted-foreground font-light text-sm leading-relaxed flex-grow">{review.text}</p>
                </div>
              );
            })}
            {visible.length === 0 && (
              <div className="col-span-full py-20 text-center text-muted-foreground/40 text-sm font-light uppercase tracking-[0.2em]">
                Загрузка отзывов…
              </div>
            )}
          </div>

          {/* Load more prompt */}
          <div className="mt-14 text-center">
            <p className="text-muted-foreground/50 text-sm font-light mb-6">{formatReviewCount(AGGREGATE.reviewCount)} отзывов на Яндексе</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={PLATFORMS.yandex.href} target="_blank" rel="noreferrer"
                className="border border-primary/30 text-muted-foreground px-8 py-4 text-[10px] tracking-[0.3em] uppercase hover:border-primary hover:text-accent transition-colors">
                Все отзывы на Яндексе →
              </a>
              <a href={PLATFORMS["2gis"].href} target="_blank" rel="noreferrer"
                className="border border-primary/30 text-muted-foreground px-8 py-4 text-[10px] tracking-[0.3em] uppercase hover:border-primary hover:text-accent transition-colors">
                Все отзывы на 2ГИС →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-card border-t border-primary/10">
        <div className="container mx-auto px-6 text-center max-w-xl">
          <div className="w-12 h-[1px] bg-primary/30 mx-auto mb-8" />
          <h2 className="font-serif text-3xl md:text-4xl font-light text-foreground mb-5">
            Станьте частью<br /><span className="text-accent italic">нашей истории</span>
          </h2>
          <p className="text-muted-foreground font-light text-sm leading-relaxed mb-10">
            Забронируйте столик и убедитесь лично — почему гости возвращаются снова и снова.
          </p>
          <button
            onClick={openBookingModal}
            className="bg-primary/20 border border-primary text-accent px-12 py-5 text-[10px] tracking-[0.35em] uppercase hover:bg-primary hover:text-background transition-colors"
          >
            Забронировать стол
          </button>
        </div>
      </section>

    </div>
  );
}
